import { computed } from 'vue';
import type { JointCanvasTemplateConfiguration } from '@/modules/ConfiguratorJoints/types/canvasTemplate';
import type { GuidePlacement, LocalShape } from '@/modules/ConfiguratorJoints/types/jointsConfiguratorTemplate';
import type { BorderLineConfig, UseInnerShaderOptions } from '@/modules/ConfiguratorJoints/types/innerShader';

const RIGHT_VERTICAL_SHADER_OVERLAP_PIXELS = 1;

function getInnerCutoffCoordinate(
    innerBorder: BorderLineConfig | null,
    innerGroup: GuidePlacement,
    isVertical: boolean,
): number | null {
    if (!innerBorder || innerBorder.points.length < 4) {
        return null;
    }

    const rotation = (innerGroup.rotation * Math.PI) / 180;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const transformPoint = (x: number, y: number) => ({
        x: innerGroup.x + x * cos - y * sin,
        y: innerGroup.y + x * sin + y * cos,
    });
    const start = transformPoint(innerBorder.points[0], innerBorder.points[1]);
    const end = transformPoint(innerBorder.points[2], innerBorder.points[3]);

    return isVertical ? (start.y + end.y) / 2 : (start.x + end.x) / 2;
}

// The dotted border closest to the outer SVG edge continues into the large
// white end box. Its far endpoint therefore gives us the box's inner border.
function getOuterCutoffCoordinate(
    borderLines: BorderLineConfig[],
    armSlices: LocalShape[],
    isVertical: boolean,
): number | null {
    if (borderLines.length < 2) {
        return null;
    }

    const crossAxisMin = Math.min(...armSlices.map((slice) => (isVertical ? slice.x : slice.y)));
    const crossAxisMax = Math.max(
        ...armSlices.map((slice) => (isVertical ? slice.x + slice.width : slice.y + slice.height)),
    );
    const distanceToOuterEdge = (line: BorderLineConfig) => {
        const crossAxisCoordinate = isVertical ? line.points[0] : line.points[1];
        return Math.min(Math.abs(crossAxisCoordinate - crossAxisMin), Math.abs(crossAxisCoordinate - crossAxisMax));
    };
    const outerBorder = [...borderLines]
        .filter((line) => line.points.length >= 4)
        .sort((first, second) => distanceToOuterEdge(first) - distanceToOuterEdge(second))[0];

    if (!outerBorder) {
        return null;
    }

    return isVertical ? outerBorder.points[3] : outerBorder.points[2];
}

function buildShaderConfig(
    armSide: 'left' | 'right',
    shading: JointCanvasTemplateConfiguration['innerSurroundShading'],
    borderLines: BorderLineConfig[],
    armSlices: LocalShape[] | null,
    innerBorder: BorderLineConfig | null,
    innerGroup: GuidePlacement,
) {
    if (!shading || borderLines.length < 2 || !armSlices?.length) {
        return null;
    }

    const [firstBorder, secondBorder] = borderLines;

    if (firstBorder.points.length < 4 || secondBorder.points.length < 4) {
        return null;
    }

    const [startX, startY, endX, endY] = firstBorder.points;
    const isVertical = Math.abs(endY - startY) > Math.abs(endX - startX);

    const sliceMinX = Math.min(...armSlices.map((slice) => slice.x));
    const sliceMaxX = Math.max(...armSlices.map((slice) => slice.x + slice.width));
    const sliceMinY = Math.min(...armSlices.map((slice) => slice.y));
    const sliceMaxY = Math.max(...armSlices.map((slice) => slice.y + slice.height));

    // Base shader: cover the full width of the rendered arm image. The inner
    // and outer cutoff stages below refine only the two ends of the arm.
    const baseShaderPoints = isVertical
        ? [sliceMinX, sliceMinY, sliceMinX, sliceMaxY, sliceMaxX, sliceMaxY, sliceMaxX, sliceMinY]
        : [sliceMinX, sliceMinY, sliceMaxX, sliceMinY, sliceMaxX, sliceMaxY, sliceMinX, sliceMaxY];

    // Inner cutoff: replace only the corner-side edge with the transformed
    // inner dotted-border coordinate. The far-side edge stays oversized.
    const innerCutoffPoints = [...baseShaderPoints];
    const innerCutoffCoordinate = getInnerCutoffCoordinate(innerBorder, innerGroup, isVertical);
    if (innerCutoffCoordinate !== null) {
        if (isVertical && armSide === 'left') {
            innerCutoffPoints[3] = innerCutoffCoordinate;
            innerCutoffPoints[5] = innerCutoffCoordinate;
        } else if (isVertical) {
            innerCutoffPoints[1] = innerCutoffCoordinate - RIGHT_VERTICAL_SHADER_OVERLAP_PIXELS;
            innerCutoffPoints[7] = innerCutoffCoordinate - RIGHT_VERTICAL_SHADER_OVERLAP_PIXELS;
        } else if (armSide === 'left') {
            innerCutoffPoints[2] = innerCutoffCoordinate;
            innerCutoffPoints[4] = innerCutoffCoordinate;
        } else {
            innerCutoffPoints[0] = innerCutoffCoordinate;
            innerCutoffPoints[6] = innerCutoffCoordinate;
        }
    }

    // Outer cutoff: stop the far-side edge at the inner border of the large
    // white end box so no tint exists underneath it or leaks past its edge.
    const outerCutoffPoints = [...innerCutoffPoints];
    const outerCutoffCoordinate = getOuterCutoffCoordinate(borderLines, armSlices, isVertical);
    if (outerCutoffCoordinate !== null) {
        if (isVertical && armSide === 'left') {
            outerCutoffPoints[1] = outerCutoffCoordinate;
            outerCutoffPoints[7] = outerCutoffCoordinate;
        } else if (isVertical) {
            outerCutoffPoints[3] = outerCutoffCoordinate;
            outerCutoffPoints[5] = outerCutoffCoordinate;
        } else if (armSide === 'left') {
            outerCutoffPoints[0] = outerCutoffCoordinate;
            outerCutoffPoints[6] = outerCutoffCoordinate;
        } else {
            outerCutoffPoints[2] = outerCutoffCoordinate;
            outerCutoffPoints[4] = outerCutoffCoordinate;
        }
    }

    return {
        points: outerCutoffPoints,
        closed: true,
        fill: shading.fill,
        opacity: shading.opacity,
        strokeEnabled: false,
        listening: false,
    };
}

export function useInnerShader({
    shading,
    leftBorderLines,
    rightBorderLines,
    leftArmSlices,
    rightArmSlices,
    leftInnerBorder,
    rightInnerBorder,
    leftInnerGroup,
    rightInnerGroup,
}: UseInnerShaderOptions) {
    const leftInnerShaderConfig = computed(() =>
        buildShaderConfig(
            'left',
            shading.value,
            leftBorderLines.value,
            leftArmSlices.value,
            leftInnerBorder.value,
            leftInnerGroup.value,
        ),
    );
    const rightInnerShaderConfig = computed(() =>
        buildShaderConfig(
            'right',
            shading.value,
            rightBorderLines.value,
            rightArmSlices.value,
            rightInnerBorder.value,
            rightInnerGroup.value,
        ),
    );

    return {
        leftInnerShaderConfig,
        rightInnerShaderConfig,
    };
}
